import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save, Plus, X } from 'lucide-react';
import Layout from '../../components/Layout';
import { parseApiError } from '../../utils/errorUtils';
import {
  purchaseOrderService,
  PurchaseOrderDTO,
  PurchaseOrderStatus,
  CreatePurchaseOrderLineRequest,
  UpdatePurchaseOrderRequest,
} from '../../services/purchaseOrderService';
import { supplierService, SupplierDTO } from '../../services/supplierService';
import { purchaseItemService, PurchaseItemDTO } from '../../services/purchaseItemService';
import './PurchaseOrderPage.css';

const NOTES_MAX_LENGTH = 500;

interface LineRow {
  purchaseItemId?: number;
  productId: number;
  productName: string;
  quantity: number;
  unitPrice: number;
  taxRate: number;   // % from purchase item — read-only
  remarks: string;
}

interface FormErrors {
  supplierId?: string;
  orderDate?: string;
  expectedDeliveryDate?: string;
  notes?: string;
  lines?: string;
}

interface LineError {
  quantity?: string;
  unitPrice?: string;
}

const emptyLine = (): LineRow => ({
  purchaseItemId: undefined,
  productId: 0,
  productName: '',
  quantity: 1,
  unitPrice: 0,
  taxRate: 0,
  remarks: '',
});

const ALL_STATUSES: PurchaseOrderStatus[] = [
  'Draft', 'Submitted', 'Confirmed', 'PartiallyReceived', 'Received', 'Closed',
];

const PurchaseOrderEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [order, setOrder] = useState<PurchaseOrderDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [lineErrors, setLineErrors] = useState<Record<number, LineError>>({});

  const [suppliers, setSuppliers] = useState<SupplierDTO[]>([]);
  const [purchaseItems, setPurchaseItems] = useState<PurchaseItemDTO[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);

  const [formData, setFormData] = useState({
    supplierId: '',
    orderDate: '',
    expectedDeliveryDate: '',
    notes: '',
    status: 'Draft' as PurchaseOrderStatus,
  });
  const [lines, setLines] = useState<LineRow[]>([emptyLine()]);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [orderRes, supplierRes] = await Promise.all([
        purchaseOrderService.getPurchaseOrderById(id || '0'),
        supplierService.getSuppliers({ pageNumber: 1, pageSize: 500 }),
      ]);

      const po = orderRes?.data?.data;
      if (po) {
        // Guard: only Draft orders with Draft approval status can be edited
        if (po.status !== 'Draft' || po.approvalStatus !== 'Draft') {
          navigate(`/purchase-orders/${po.id}`, {
            replace: true,
            state: { errorMsg: t('purchaseOrder.editNotAllowed', 'This purchase order can no longer be edited. Only Draft orders that have not been submitted for approval can be modified.') },
          });
          return;
        }

        setOrder(po);
        setFormData({
          supplierId: String(po.supplierId),
          orderDate: po.orderDate.slice(0, 10),
          expectedDeliveryDate: po.expectedDeliveryDate?.slice(0, 10) ?? '',
          notes: po.notes || '',
          status: po.status,
        });

        // Load purchase items for this supplier first so we can match purchaseItemId
        let items: PurchaseItemDTO[] = [];
        if (po.supplierId) {
          try {
            const itemsRes = await purchaseItemService.getPurchaseItemsBySupplier(po.supplierId);
            const itemsData = itemsRes?.data?.data ?? itemsRes?.data;
            if (Array.isArray(itemsData)) {
              items = itemsData;
              setPurchaseItems(items);
            }
          } catch {
            // non-fatal — product dropdown will fall back to text input
          }
        }

        if (po.lines && po.lines.length > 0) {
          setLines(po.lines.map((line) => {
            const matched = items.find((p) => p.productId === line.productId);
            return {
              purchaseItemId: matched?.purchaseItemId,
              productId: line.productId,
              productName: line.productName ?? '',
              quantity: line.quantity,
              unitPrice: line.unitPrice,
              taxRate: matched?.taxPercentage ?? 0,
              remarks: (line as any).remarks ?? '',
            };
          }));
        }
      }

      const supplierData = supplierRes?.data?.data;
      if (Array.isArray(supplierData)) setSuppliers(supplierData);
    } catch (err: unknown) {
      setError(parseApiError(err, t('purchaseOrder.failedToLoadOrder')).message);
    } finally {
      setLoading(false);
    }
  };

  const loadPurchaseItems = async (supplierId: number) => {
    setLoadingItems(true);
    try {
      const res = await purchaseItemService.getPurchaseItemsBySupplier(supplierId);
      const data = res?.data?.data ?? res?.data;
      if (Array.isArray(data)) setPurchaseItems(data);
    } catch {
      setPurchaseItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleSupplierChange = (supplierId: string) => {
    setFormData((prev) => ({ ...prev, supplierId }));
    setFormErrors((prev) => ({ ...prev, supplierId: undefined }));
    setLines([emptyLine()]);
    setLineErrors({});
    setPurchaseItems([]);
    if (supplierId) loadPurchaseItems(Number(supplierId));
  };

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === 'supplierId') {
      handleSupplierChange(value);
      return;
    }

    if (name === 'status') {
      setFormData((prev) => ({ ...prev, status: value as PurchaseOrderStatus }));
      return;
    }

    let finalValue = value;
    if (name === 'notes' && value.length > NOTES_MAX_LENGTH) {
      finalValue = value.substring(0, NOTES_MAX_LENGTH);
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    setFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleLineItemSelect = (index: number, purchaseItemId: string) => {
    const item = purchaseItems.find((p) => p.purchaseItemId === Number(purchaseItemId));
    setLines((prev) => {
      const updated = [...prev];
      if (item) {
        updated[index] = {
          purchaseItemId: item.purchaseItemId,
          productId: item.productId,
          productName: item.productName,
          quantity: updated[index].quantity || 1,
          unitPrice: item.purchasePrice,
          taxRate: item.taxPercentage ?? 0,
          remarks: updated[index].remarks,
        };
      } else {
        updated[index] = emptyLine();
      }
      return updated;
    });
    setLineErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setFormErrors((prev) => ({ ...prev, lines: undefined }));
  };

  const handleLineChange = (index: number, field: 'quantity' | 'unitPrice' | 'remarks', value: string) => {
    setLines((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: field === 'remarks' ? value : (parseFloat(value) || 0),
      };
      return updated;
    });
    setLineErrors((prev) => ({
      ...prev,
      [index]: { ...prev[index], [field]: undefined },
    }));
  };

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);

  const removeLine = (index: number) => {
    if (lines.length > 1) {
      setLines((prev) => prev.filter((_, i) => i !== index));
      setLineErrors((prev) => {
        const next: Record<number, LineError> = {};
        Object.entries(prev).forEach(([key, val]) => {
          const k = Number(key);
          if (k < index) next[k] = val;
          else if (k > index) next[k - 1] = val;
        });
        return next;
      });
    }
  };

  const lineTaxAmt  = (l: LineRow) => l.quantity * l.unitPrice * (l.taxRate / 100);
  const lineTotal   = (l: LineRow) => l.quantity * l.unitPrice + lineTaxAmt(l);
  const subtotal    = lines.reduce((s, l) => s + l.quantity * l.unitPrice, 0);
  const totalTax    = lines.reduce((s, l) => s + lineTaxAmt(l), 0);
  const grandTotal  = subtotal + totalTax;

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    const newLineErrors: Record<number, LineError> = {};
    let isValid = true;

    if (!formData.supplierId) {
      errors.supplierId = t('purchaseOrder.formValidation.supplierRequired');
      isValid = false;
    }

    if (!formData.orderDate) {
      errors.orderDate = t('purchaseOrder.formValidation.orderDateRequired');
      isValid = false;
    }

    if (!formData.expectedDeliveryDate) {
      errors.expectedDeliveryDate = t('purchaseOrder.formValidation.deliveryDateRequired');
      isValid = false;
    } else if (formData.orderDate && formData.expectedDeliveryDate < formData.orderDate) {
      errors.expectedDeliveryDate = t('purchaseOrder.formValidation.deliveryBeforeOrder');
      isValid = false;
    }

    if (formData.notes && formData.notes.length > NOTES_MAX_LENGTH) {
      errors.notes = t('purchaseOrder.formValidation.notesMaxLength');
      isValid = false;
    }

    const validLines = lines.filter((l) => l.productId > 0);
    if (validLines.length === 0) {
      errors.lines = t('purchaseOrder.formValidation.itemsRequired');
      isValid = false;
    } else {
      lines.forEach((l, index) => {
        if (l.productId > 0) {
          const lineErr: LineError = {};
          if (l.quantity <= 0) {
            lineErr.quantity = t('purchaseOrder.formValidation.quantityRequired');
            isValid = false;
          }
          if (l.unitPrice < 0) {
            lineErr.unitPrice = t('purchaseOrder.formValidation.unitPriceNegative');
            isValid = false;
          }
          if (Object.keys(lineErr).length > 0) {
            newLineErrors[index] = lineErr;
          }
        }
      });
    }

    setFormErrors(errors);
    setLineErrors(newLineErrors);

    if (!isValid) {
      const firstHeaderError = Object.values(errors).find((e) => e);
      const firstLineError = Object.values(newLineErrors)
        .flatMap((e) => Object.values(e))
        .find((e) => e);
      const firstError = firstHeaderError || firstLineError;
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setFormErrors({});
    setLineErrors({});
    if (!validateForm() || !order) return;

    const lines_payload: CreatePurchaseOrderLineRequest[] = lines
      .filter((l) => l.productId > 0)
      .map((l) => ({
        productId: l.productId,
        quantity: l.quantity,
        unitPrice: l.unitPrice,
        remarks: l.remarks || undefined,
      }));

    const payload: UpdatePurchaseOrderRequest = {
      supplierId: Number(formData.supplierId),
      orderDate: formData.orderDate,
      expectedDeliveryDate: formData.expectedDeliveryDate || undefined,
      notes: formData.notes || undefined,
      status: formData.status,
      lines: lines_payload,
    };

    setSaving(true);
    try {
      await purchaseOrderService.updatePurchaseOrder(order.id, payload);
      navigate(`/purchase-orders/${order.id}`, {
        state: { message: t('purchaseOrder.purchaseOrderUpdatedSuccess') },
      });
    } catch (err: unknown) {
      setError(parseApiError(err, t('purchaseOrder.failedToUpdateOrder')).message);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="ims-loading">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div style={{ padding: '32px 28px' }}>
          <div className="ims-alert ims-alert-error">{error || t('purchaseOrder.purchaseOrderNotFound')}</div>
          <button className="ims-back-btn" onClick={() => navigate('/purchase-orders')}>
            {t('purchaseOrder.backToPurchaseOrders')}
          </button>
        </div>
      </Layout>
    );
  }

  const langCode = t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US';

  const fmt = (n: number) =>
    new Intl.NumberFormat(langCode, { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  const validItemCount = lines.filter((l) => l.productId > 0).length;

  return (
    <Layout>
      <form onSubmit={handleSubmit} className="po-create-page" noValidate>

        {/* ── Top bar ── */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button
              type="button"
              className="ims-back-btn"
              onClick={() => navigate(`/purchase-orders/${id}`)}
            >
              <ArrowLeft size={14} strokeWidth={2} />
              {t('purchaseOrder.backToPurchaseOrder')}
            </button>
            <div>
              <h1 className="ims-page-title">{t('purchaseOrder.editTitle')}</h1>
              <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
                <code>{order.poNumber}</code>
              </p>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => navigate(`/purchase-orders/${id}`)}
              disabled={saving}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? (
                <><span className="ims-btn-spinner" />{t('common.saving')}</>
              ) : (
                <>
                  <Save size={15} strokeWidth={2} />
                  {t('common.saveChanges')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div className="ims-error-banner ims-alert ims-alert-error">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="ims-banner-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            >
              <X size={14} strokeWidth={2.5} />
            </button>
          </div>
        )}

        {/* ── 3-column body ── */}
        <div className="ims-create-body">

          {/* LEFT — Order Info */}
          <div className="ims-panel-left">
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">{t('purchaseOrder.orderInfo')}</h2>
              </div>
              <div className="ims-card-body">

                {/* Supplier */}
                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('purchaseOrder.supplier')} <span className="required">*</span>
                  </label>
                  <select
                    className={`ims-form-control ${formErrors.supplierId ? 'is-invalid' : ''}`}
                    name="supplierId"
                    value={formData.supplierId}
                    onChange={handleFormChange}
                  >
                    <option value="">{t('purchaseOrder.selectSupplier')}</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                    ))}
                  </select>
                  {formErrors.supplierId && <span className="ims-field-error">{formErrors.supplierId}</span>}
                </div>

                {/* Dates */}
                <div className="ims-date-row">
                  <div className="ims-form-group">
                    <label className="ims-form-label">
                      {t('purchaseOrder.orderDate')} <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      className={`ims-form-control ${formErrors.orderDate ? 'is-invalid' : ''}`}
                      name="orderDate"
                      value={formData.orderDate}
                      onChange={handleFormChange}
                    />
                    {formErrors.orderDate && <span className="ims-field-error">{formErrors.orderDate}</span>}
                  </div>
                  <div className="ims-form-group">
                    <label className="ims-form-label">
                      {t('purchaseOrder.expectedDelivery')} <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      className={`ims-form-control ${formErrors.expectedDeliveryDate ? 'is-invalid' : ''}`}
                      name="expectedDeliveryDate"
                      value={formData.expectedDeliveryDate}
                      onChange={handleFormChange}
                    />
                    {formErrors.expectedDeliveryDate && <span className="ims-field-error">{formErrors.expectedDeliveryDate}</span>}
                  </div>
                </div>

                {/* Status */}
                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('purchaseOrder.orderStatus')} <span className="required">*</span>
                  </label>
                  <select
                    className="ims-form-control"
                    name="status"
                    value={formData.status}
                    onChange={handleFormChange}
                  >
                    {ALL_STATUSES.map((s) => (
                      <option key={s} value={s}>{t(`purchaseOrder.status${s}`)}</option>
                    ))}
                  </select>
                </div>

                {/* Notes */}
                <div className="ims-form-group">
                  <label className="ims-form-label">{t('purchaseOrder.notes')}</label>
                  <textarea
                    className={`ims-form-control ims-textarea ${formErrors.notes ? 'is-invalid' : ''}`}
                    name="notes"
                    value={formData.notes}
                    onChange={handleFormChange}
                    rows={3}
                    maxLength={NOTES_MAX_LENGTH}
                  />
                  {formErrors.notes && <span className="ims-field-error">{formErrors.notes}</span>}
                  {!formErrors.notes && (
                    <span className="ims-field-helper">
                      {formData.notes.length}/{NOTES_MAX_LENGTH}
                    </span>
                  )}
                </div>

              </div>
            </div>
          </div>

          {/* CENTER — Line Items */}
          <div className="ims-panel-center">
            <div className="ims-card ims-lines-card">
              <div className="ims-lines-header">
                <h2 className="ims-card-title">{t('purchaseOrder.lineItems')}</h2>
                {loadingItems && <span>{t('common.loading')}</span>}
                <button type="button" className="ims-btn ims-btn-secondary" onClick={addLine}>
                  <Plus size={13} strokeWidth={2} />
                  {t('purchaseOrder.addLineItem')}
                </button>
              </div>

              {formErrors.lines && (
                <div style={{ padding: '6px 16px 0' }}>
                  <span className="ims-field-error">{formErrors.lines}</span>
                </div>
              )}

              <div className="ims-lines-body">
                <div className="ims-table-scroll">
                  <table className="ims-table">
                    <thead>
                      <tr>
                        <th className="ims-th">{t('purchaseOrder.product')}</th>
                        <th className="ims-th ims-th--num">{t('purchaseOrder.quantity')}</th>
                        <th className="ims-th ims-th--num">{t('purchaseOrder.unitPrice')}</th>
                        <th className="ims-th ims-th--num">{t('purchaseOrder.taxRate', 'Tax %')}</th>
                        <th className="ims-th ims-th--num">{t('purchaseOrder.taxAmount', 'Tax Amt')}</th>
                        <th className="ims-th ims-th--num">{t('purchaseOrder.lineTotal')}</th>
                        <th className="ims-th">{t('purchaseOrder.remarks', 'Remarks')}</th>
                        <th className="ims-th ims-th--del" />
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line, index) => {
                        const tax = lineTaxAmt(line);
                        const total = lineTotal(line);
                        return (
                          <tr key={index} className="ims-tr">
                            <td className="ims-td">
                              {purchaseItems.length > 0 ? (
                                <select
                                  className="ims-cell-input"
                                  value={line.purchaseItemId ?? ''}
                                  onChange={(e) => handleLineItemSelect(index, e.target.value)}
                                >
                                  <option value="">{t('purchaseOrder.selectProduct')}</option>
                                  {purchaseItems.map((p) => (
                                    <option key={p.purchaseItemId} value={p.purchaseItemId}>
                                      {p.productName} ({p.productSKU})
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  className="ims-cell-input"
                                  placeholder={line.productName || (formData.supplierId ? t('purchaseOrder.noItemsForSupplier') : t('purchaseOrder.selectSupplierFirst'))}
                                  value={line.productName}
                                  onChange={(e) =>
                                    setLines((prev) => {
                                      const updated = [...prev];
                                      updated[index] = { ...updated[index], productName: e.target.value };
                                      return updated;
                                    })
                                  }
                                  disabled={!formData.supplierId || loadingItems}
                                />
                              )}
                            </td>
                            <td className="ims-td ims-td--num">
                              <input
                                type="number"
                                className={`ims-cell-input ims-num-input ${lineErrors[index]?.quantity ? 'is-invalid' : ''}`}
                                value={line.quantity}
                                onChange={(e) => handleLineChange(index, 'quantity', e.target.value)}
                              />
                              {lineErrors[index]?.quantity && (
                                <div className="ims-field-error">{lineErrors[index]?.quantity}</div>
                              )}
                            </td>
                            <td className="ims-td ims-td--num">
                              <input
                                type="number"
                                className={`ims-cell-input ims-num-input ${lineErrors[index]?.unitPrice ? 'is-invalid' : ''}`}
                                step={0.01}
                                value={line.unitPrice}
                                onChange={(e) => handleLineChange(index, 'unitPrice', e.target.value)}
                              />
                              {lineErrors[index]?.unitPrice && (
                                <div className="ims-field-error">{lineErrors[index]?.unitPrice}</div>
                              )}
                            </td>
                            <td className="ims-td ims-td--num po-tax-cell">
                              {line.taxRate > 0 ? `${line.taxRate}%` : '—'}
                            </td>
                            <td className="ims-td ims-td--num po-tax-cell">
                              {tax > 0 ? fmt(tax) : '—'}
                            </td>
                            <td className="ims-td ims-td--num">
                              {fmt(total)}
                            </td>
                            <td className="ims-td">
                              <input
                                type="text"
                                className="ims-cell-input"
                                placeholder={t('purchaseOrder.remarksPlaceholder', 'Optional note…')}
                                value={line.remarks}
                                onChange={(e) => handleLineChange(index, 'remarks', e.target.value)}
                              />
                            </td>
                            <td className="ims-td ims-td--del">
                              <button
                                type="button"
                                className="ims-remove-btn"
                                onClick={() => removeLine(index)}
                                disabled={lines.length === 1}
                                title={t('purchaseOrder.removeLineItem')}
                              >
                                <X size={14} strokeWidth={2} />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={5} className="ims-td ims-td--num po-tfoot-label">{t('purchaseOrder.subtotal', 'Subtotal')}</td>
                        <td className="ims-td ims-td--num po-tfoot-value">{fmt(subtotal)}</td>
                        <td colSpan={2} className="ims-td ims-td--del" />
                      </tr>
                      <tr>
                        <td colSpan={5} className="ims-td ims-td--num po-tfoot-label">{t('purchaseOrder.totalTax', 'Total Tax')}</td>
                        <td className="ims-td ims-td--num po-tfoot-value">{fmt(totalTax)}</td>
                        <td colSpan={2} className="ims-td ims-td--del" />
                      </tr>
                      <tr className="po-tfoot-grand">
                        <td colSpan={5} className="ims-td ims-td--num po-tfoot-label">{t('purchaseOrder.total', 'Total')}</td>
                        <td className="ims-td ims-td--num po-tfoot-total">{fmt(grandTotal)}</td>
                        <td colSpan={2} className="ims-td ims-td--del" />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — Summary + Audit */}
          <div className="ims-panel-right">
            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('purchaseOrder.orderSummary')}</h3>
              </div>
              <div className="ims-card-body">
                <div className="ims-summary-row">
                  <span className="ims-summary-label">{t('purchaseOrder.itemCount')}</span>
                  <span className="ims-summary-value">{validItemCount}</span>
                </div>
                <hr style={{ margin: '8px 0', borderColor: '#f3f4f6' }} />
                <div className="ims-summary-row">
                  <span>{t('purchaseOrder.subtotal', 'Subtotal')}</span>
                  <span>{fmt(subtotal)}</span>
                </div>
                <div className="ims-summary-row">
                  <span>{t('purchaseOrder.totalTax', 'Total Tax')}</span>
                  <span>{fmt(totalTax)}</span>
                </div>
                <hr style={{ margin: '8px 0', borderColor: '#f3f4f6' }} />
                <div className="ims-summary-row ims-summary-total">
                  <span>{t('purchaseOrder.total', 'Total')}</span>
                  <span>{fmt(grandTotal)}</span>
                </div>
              </div>
            </div>

            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('purchaseOrder.auditInformation')}</h3>
              </div>
              <div className="ims-card-body">
                <div className="ims-summary-row" style={{ marginBottom: 6 }}>
                  <span className="ims-summary-label">{t('purchaseOrder.purchaseOrderId')}</span>
                  <span className="ims-summary-value">{order.id}</span>
                </div>
                <div className="ims-summary-row">
                  <span className="ims-summary-label">{t('purchaseOrder.createdDate')}</span>
                  <span className="ims-summary-value" style={{ fontSize: 11 }}>
                    {new Date(order.createdAt).toLocaleDateString(langCode)}
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </form>
    </Layout>
  );
};

export default PurchaseOrderEditPage;
